/**
 * T16201 · `connectionAction` mutex — STABLE | REFRESHING | RECONNECTING | DEGRADED.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * §L1505.
 *
 * Second state machine (distinct from the 4-state connectivity FSM in
 * `state-machine.ts`) that serialises JWT-refresh and transport-reconnect
 * attempts to prevent the nats.ws #61 ghost-client class.
 *
 * STATES
 *   - `STABLE`        · idle.
 *   - `REFRESHING`    · JWT refresh ladder in flight; transport must wait.
 *   - `RECONNECTING`  · `nc.reconnect()` in flight; refresh must wait.
 *   - `DEGRADED`      · terminal sub-state after both ladders exhausted.
 *
 * Pinned to `globalThis.__morfi_connectionAction` so the mutex survives
 * HMR / vi.resetModules — both T305 (refresh ladder) and T303
 * (connection-promise singleton) route through this surface.
 */

export type ConnectionAction = 'STABLE' | 'REFRESHING' | 'RECONNECTING' | 'DEGRADED';

interface MutexState {
  state: ConnectionAction;
  inFlight: Promise<void> | null;
  inFlightOf: 'refresh' | 'reconnect' | null;
  cause: string | null;
}

declare global {
  var __morfi_connectionAction: { s: MutexState } | undefined;
}

function store(): { s: MutexState } {
  let slot = globalThis.__morfi_connectionAction;
  if (slot === undefined) {
    slot = { s: { state: 'STABLE', inFlight: null, inFlightOf: null, cause: null } };
    globalThis.__morfi_connectionAction = slot;
  }
  return slot;
}

export function getConnectionAction(): ConnectionAction {
  return store().s.state;
}

/**
 * Enqueue an action body. Same-kind concurrent calls COALESCE — both
 * callers receive the SAME `Promise<void>` reference (`.toBe` equality
 * passes). Cross-kind calls await STABLE (or DEGRADED) before
 * transitioning. Terminal `DEGRADED` drops any further requests.
 *
 * NOT declared `async` so the coalesce path returns the in-flight
 * promise BY REFERENCE rather than wrapping it in a fresh
 * `Promise.resolve`.
 */
export function requestAction(
  kind: 'refresh' | 'reconnect',
  body: () => Promise<void>,
): Promise<void> {
  const slot = store();

  // Coalesce: same kind already running → return that promise (same ref).
  if (slot.s.inFlight !== null && slot.s.inFlightOf === kind) {
    return slot.s.inFlight;
  }

  // Cross-kind / cold-start path. Capture the in-flight promise BEFORE
  // the async closure starts so the closure's wait-loop can compare
  // against it via the captured local (not the slot field, which will
  // be reassigned to point at us before any await yields).
  const priorInFlight = slot.s.inFlight;
  const ourPromise = (async (): Promise<void> => {
    if (priorInFlight !== null) {
      try {
        await priorInFlight;
      } catch {
        // ignore — we re-check state below
      }
    }
    if (slot.s.state === 'DEGRADED') return;

    slot.s.state = kind === 'refresh' ? 'REFRESHING' : 'RECONNECTING';
    try {
      await body();
    } finally {
      // `body()` may have mutated `slot.s.state` (e.g. to DEGRADED on
      // exhaustion). Re-read through a widened alias so TS does not
      // narrow back to the post-assignment literal type.
      const currentState: string = slot.s.state;
      if (currentState !== 'DEGRADED') slot.s.state = 'STABLE';
      slot.s.inFlight = null;
      slot.s.inFlightOf = null;
    }
  })();

  slot.s.inFlight = ourPromise;
  slot.s.inFlightOf = kind;
  return ourPromise;
}

export function markStable(): void {
  const slot = store();
  if (slot.s.state !== 'DEGRADED') slot.s.state = 'STABLE';
}

export function markDegraded(cause: string): void {
  const slot = store();
  slot.s.state = 'DEGRADED';
  slot.s.cause = cause;
}

/** Test seam — wipes the singleton so each test starts STABLE. */
export function _resetConnectionActionForTest(): void {
  globalThis.__morfi_connectionAction = undefined;
}
